import React, { useState } from 'react';
import {
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonInput,
  IonText,
  IonListHeader,
} from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import { PageLayout } from 'containers/layout';
import { Tip } from 'components/tab';
import CurrentPaymentItem from 'containers/payment/CurrentPaymentItem';
import PortionSelect from 'containers/tab/PortionSelect';
import { formatCurrency, calcTip } from 'utils/currency';
import { AppSelectors } from 'redux/AppRedux';
import PayActions, { PaySelectors } from 'redux/PayRedux';

function PayTab() {
  const [percent, setPercent] = useState('20');
  const [custom, setCustom] = useState('');
  const [cvv, setCvv] = useState('');
  const ticket = useSelector(PaySelectors.selectTicket);
  const venue = useSelector(AppSelectors.selectVenueDetail);
  const userCount = useSelector(PaySelectors.selectTicketUserCount);
  const dispatch = useDispatch();
  const paying = useSelector(PaySelectors.selectPaying);
  const [portion, setPortion] = useState(1);
  const onePortion = useSelector(PaySelectors.selectMyPortion);
  const unpaidUsers = useSelector(PaySelectors.selectUnpaidUserCount);
  const payType = useSelector(PaySelectors.selectPayType);
  const myPrice = useSelector(PaySelectors.selectMyPrice);
  const activeMethod = useSelector(PaySelectors.selectActivePaymentMethod);

  if (!ticket) {
    return null;
  }

  const automaticGratuity = !!venue.automaticGratuity;
  const isLast = unpaidUsers === 1;
  const subtotalAmount = isLast
    ? myPrice
    : ticket.totals
    ? ticket.totals.subTotal + ticket.totals.discounts
    : 0;

  const multiplier =
    venue.isFullPay || isLast
      ? 1
      : payType === 'portion'
      ? onePortion * portion
      : (1 / subtotalAmount) * myPrice;

  const tips = calcTip(
    subtotalAmount * multiplier,
    automaticGratuity && custom ? 'custom' : percent,
    custom,
    automaticGratuity
  );
  const isCvvActive =
    venue.posType === 'clover' ||
    (venue.posType === 'toast' && activeMethod && !activeMethod.cardUsedInToast);

  const handlePayment = () => {
    dispatch(PayActions.makePayment(tips, portion, cvv));
  };

  const hasPortion = userCount > 1 || (ticket.payments || []).length > 0;
  const totalAmount = isLast ? ticket.totals.due : ticket.totals.total;

  return (
    <PageLayout
      title="Pay"
      back
      footer={
        <IonButton
          color="tertiary"
          expand="block"
          fill="solid"
          onClick={handlePayment}
          disabled={isCvvActive && !cvv}
        >
          {ticket && ticket.is_pickup ? 'Send Order' : 'Pay Now'}
        </IonButton>
      }
    >
      <IonLoading isOpen={paying} message="Making Payment..." />
      {!venue.isFullPay && payType === 'portion' && (
        <PortionSelect
          value={portion}
          onChange={setPortion}
          max={userCount}
          unpaidUsers={unpaidUsers}
        />
      )}
      <CurrentPaymentItem />
      {isCvvActive && (
        <IonItem lines="full">
          <IonLabel position="stacked">CVV</IonLabel>
          <IonInput
            name="cvv"
            placeholder="CVV"
            type="tel"
            disabled={paying}
            onIonChange={(e) => setCvv(e.target.value)}
            value={cvv}
            maxlength={4}
          />
          {(!cvv || cvv.length < 3) && (
            <IonText color="danger">
              <small>CVV is required.</small>
            </IonText>
          )}
        </IonItem>
      )}
      <Tip
        option={percent}
        setOption={setPercent}
        value={custom}
        setValue={setCustom}
        automaticGratuity={automaticGratuity}
      />
      <IonList>
        <IonListHeader>Payment Details</IonListHeader>
        {hasPortion && (
          <IonItem lines="none">
            <IonLabel slot="start" color="medium">
              Subtotal
            </IonLabel>
            <IonLabel color="medium" className="menuitem-price">
              {ticket.totals && formatCurrency(ticket.totals.subTotal)}
            </IonLabel>
          </IonItem>
        )}

        <IonItem lines="none">
          <IonLabel slot="start">
            {hasPortion ? 'My Portion' : 'Subtotal'}
          </IonLabel>
          <IonLabel className="menuitem-price">
            {formatCurrency(subtotalAmount * multiplier)}
          </IonLabel>
        </IonItem>

        {!!ticket.totals.discounts && (
          <IonItem lines="none">
            <IonLabel slot="start">Discounts</IonLabel>
            <IonLabel className="menuitem-price">
              -{formatCurrency(ticket.totals.discounts * multiplier)}
            </IonLabel>
          </IonItem>
        )}
        {!!ticket.totals.gratuity && (
          <IonItem lines="none">
            <IonLabel slot="start">Automatic Gratuity</IonLabel>
            <IonLabel className="menuitem-price">
              {formatCurrency(ticket.totals.gratuity * multiplier)}
            </IonLabel>
          </IonItem>
        )}
        <IonItem lines="none">
          <IonLabel slot="start">Tip</IonLabel>
          <IonLabel className="menuitem-price">{formatCurrency(tips)}</IonLabel>
        </IonItem>
        {!!ticket.totals.otherCharges && (
          <IonItem lines="none">
            <IonLabel slot="start">Additional Charge</IonLabel>
            <IonLabel className="menuitem-price">
              {formatCurrency(ticket.totals.otherCharges * multiplier)}
            </IonLabel>
          </IonItem>
        )}
        {!!ticket.totals.serviceCharges && (
          <IonItem lines="none">
            <IonLabel slot="start">Service Fee</IonLabel>
            <IonLabel className="menuitem-price">
              {formatCurrency(ticket.totals.serviceCharges * multiplier)}
            </IonLabel>
          </IonItem>
        )}
        <IonItem lines="full">
          <IonLabel slot="start">Tax</IonLabel>
          <IonLabel className="menuitem-price">
            {formatCurrency(ticket.totals.tax * multiplier)}
          </IonLabel>
        </IonItem>
        <IonItem lines="none">
          <IonLabel slot="start">
            <h3>Total</h3>
          </IonLabel>
          <IonLabel className="menuitem-price">
            <h3>{formatCurrency(tips + totalAmount * multiplier)}</h3>
          </IonLabel>
        </IonItem>
      </IonList>
    </PageLayout>
  );
}

export default PayTab;
